/* eslint-disable prefer-promise-reject-errors */
import type {
    GenerateRecommendedVideoResponse,
    SceneRecommendation,
    TaskResultResponse,
} from 'js/src/client';
import { ApiService, TaskResultStatusEnum } from 'js/src/client';
import type { ApiError } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { ErrorObject } from 'js/src/types';
import { MaxRequestsError, OperationAbortedError } from 'js/src/types';

const MAX_FAILURES = 3; // number of tries
export const POLL_FREQUENCY = 1500; // milliseconds
const MAX_REQUESTS = 300; // ~10 min max tolerance
export const IS_PARTIAL_RESPONSE = true;

export type PartialResultCallback<T> = (partial_result: T, is_partial_response?: boolean) => void;

/*
 * Our DRF serializer returns a list of nullable items. However, we have not been able to figure out how
 * to generate a type that represents a list of nullable items. Even when modifying the openapi schema
 * to include the nullable keyword, the generated typescript types do not reflect this
 */
export type GenerateRecommendedVideoResponseWithNullableScenes = Omit<
    GenerateRecommendedVideoResponse,
    'scenes'
> & {
    scenes: Array<SceneRecommendation | null>;
};
type PatchType<Union, TypeToPatch, PatchedType> = Union extends TypeToPatch ? PatchedType : Union;

export type PatchedTaskResultResponse = PatchType<
    TaskResultResponse,
    GenerateRecommendedVideoResponse,
    GenerateRecommendedVideoResponseWithNullableScenes
>;

class PollAsyncTask {
    // keep track of the different task_ids we're polling and their corresponding params
    static polls: Record<
        string,
        {
            partial_response_callback: PartialResultCallback<PatchedTaskResultResponse> | undefined;
            resolve: (
                value: PatchedTaskResultResponse | PromiseLike<PatchedTaskResultResponse>,
            ) => void;
            reject: (reason: ErrorObject) => void;
            abort_controller: AbortController | null;
            fail_count: number;
            num_requests: number;
            max_requests: number;
            task_name: string;
        }
    > = {};

    static async _do_poll() {
        // first, check if any tasks have been aborted
        const task_ids_to_remove = [];
        for (const task_id in this.polls) {
            const poll = this.polls[task_id];
            const { task_name } = poll;

            // Stop polling when we receive the abort signal
            if (poll.abort_controller && poll.abort_controller.signal.aborted) {
                poll.reject({
                    error: new OperationAbortedError(poll.abort_controller.signal.reason),
                });
                task_ids_to_remove.push(task_id);
                continue;
            }

            if (poll.num_requests > poll.max_requests) {
                poll.reject({
                    error: new MaxRequestsError(`Request maximum reached for ${task_name}`),
                });
                task_ids_to_remove.push(task_id);
            }
        }
        for (const task_id of task_ids_to_remove) {
            delete this.polls[task_id];
        }
        if (Object.keys(this.polls).length === 0) {
            return;
        }

        // fetch the results for each task
        const comma_separated_task_ids = Object.keys(this.polls).join(',');
        try {
            const results = await ApiService.taskResultsList({
                query: { task_ids: comma_separated_task_ids },
            });
            for (const result of results.data) {
                const poll = this.polls[result.task_id];

                if (!poll) {
                    // It's possible the task was removed while we were polling
                    continue;
                }

                const { task_name } = poll;
                if (result.status === TaskResultStatusEnum.PENDING) {
                    if (poll.partial_response_callback && result.partial_result) {
                        poll.partial_response_callback(
                            result.partial_result as PatchedTaskResultResponse,
                            IS_PARTIAL_RESPONSE,
                        );
                    }
                    continue;
                } else if (result.status === TaskResultStatusEnum.SUCCESS) {
                    poll.resolve(result.result as PatchedTaskResultResponse);
                    task_ids_to_remove.push(result.task_id);
                } else if (result.status === TaskResultStatusEnum.FAILURE) {
                    const extraData = result.result ? { result: result.result } : {};
                    poll.reject({
                        error: Error(`${result.message} - ${task_name}`),
                        ...extraData,
                    });
                    task_ids_to_remove.push(result.task_id);
                }

                performance.measure?.(task_name, {
                    start: `poll_async_task_start_${result.task_id}`,
                    detail: {
                        status: result.status,
                        task_id: result.task_id,
                    },
                });
            }
        } catch (error: unknown) {
            // something went wrong with the request!
            ErrorReporter.capture(error);
            for (const task_id in this.polls) {
                const poll = this.polls[task_id];
                poll.fail_count++;
                if (poll.fail_count >= MAX_FAILURES) {
                    poll.reject({ error: error as ApiError });
                    task_ids_to_remove.push(task_id);
                }
            }
        }

        for (const task_id of task_ids_to_remove) {
            delete this.polls[task_id];
        }

        // if there are still tasks to poll, continue polling
        if (Object.keys(this.polls).length > 0) {
            for (const task_id in this.polls) {
                const poll = this.polls[task_id];
                poll.num_requests++;
            }
            window.setTimeout(() => {
                this._do_poll();
            }, POLL_FREQUENCY);
        }
    }

    /**
     * Polls the server for the result of mulitple async tasks. Returns a list of promises
     * Each promise will resolves with the result of the task (or rejects if it times out or fails).
     * Supports progressive loading (where the server returns a partial response before the task is fully complete)
     * via the partial_response_callback function.
     */
    static bulk_poll_for_results(
        taskArgs: Array<{
            task_id: string;
            task_name: string;
            partial_response_callback?:
                | PartialResultCallback<PatchedTaskResultResponse>
                | undefined;
            abort_controller?: AbortController | null;
        }>,
        poll_once = false,
    ) {
        const promises: Promise<PatchedTaskResultResponse>[] = taskArgs.map((taskArg) => {
            const {
                task_id,
                task_name,
                partial_response_callback = undefined,
                abort_controller = null,
            } = taskArg;
            return new Promise((resolve, reject) => {
                if (abort_controller && abort_controller.signal.aborted) {
                    reject({
                        error: new OperationAbortedError(abort_controller.signal.reason),
                    });
                    return;
                }

                performance.mark?.(`poll_async_task_start_${task_id}`);
                const max_requests = poll_once ? 1 : MAX_REQUESTS;

                PollAsyncTask.polls[task_id] = {
                    partial_response_callback:
                        partial_response_callback as PartialResultCallback<PatchedTaskResultResponse>,
                    resolve: resolve as (
                        value: PatchedTaskResultResponse | PromiseLike<PatchedTaskResultResponse>,
                    ) => void,
                    reject,
                    abort_controller,
                    fail_count: 0,
                    num_requests: 1,
                    max_requests,
                    task_name,
                };
            });
        });
        void PollAsyncTask._do_poll();
        return promises;
    }

    /**
     * Polls the server for the result of an async task. Returns a promise which resolves with the result of the
     * task (or rejects if it times out or fails).
     * Supports progressive loading (where the server returns a partial response before the task is fully complete)
     * via the partial_response_callback function.
     */
    static poll_for_result<ResponseType extends PatchedTaskResultResponse>(
        task_id: string,
        task_name: string,
        partial_response_callback: PartialResultCallback<ResponseType> | undefined = undefined,
        abort_controller: AbortController | null = null,
        poll_once = false,
    ): Promise<ResponseType> {
        return new Promise((resolve, reject) => {
            // check if, for some reason, the task_id has already been aborted
            if (abort_controller && abort_controller.signal.aborted) {
                reject({
                    error: new OperationAbortedError(abort_controller.signal.reason),
                });
                return;
            }

            // add to polls
            performance.mark?.(`poll_async_task_start_${task_id}`);
            const max_requests = poll_once ? 1 : MAX_REQUESTS;
            PollAsyncTask.polls[task_id] = {
                partial_response_callback:
                    partial_response_callback as PartialResultCallback<PatchedTaskResultResponse>,
                resolve: resolve as (
                    value: PatchedTaskResultResponse | PromiseLike<PatchedTaskResultResponse>,
                ) => void,
                reject,
                abort_controller,
                fail_count: 0,
                num_requests: 1,
                max_requests,
                task_name,
            };

            // if only one task is being polled we need to kick off polling (otherwise we'll already have
            // a polling loop running)
            if (Object.keys(PollAsyncTask.polls).length === 1) {
                void PollAsyncTask._do_poll();
            }
        });
    }

    /**
     * Revokes an async task.
     * @param task_id – the task id to revoke (required)
     * @returns a promise that resolves when the task is successfully revoked
     * @throws an error if the task cannot be revoked
     */
    static revoke_task(task_id: string): Promise<void> {
        return new Promise((resolve, reject) => {
            ApiService.taskResultDestroy({ path: { task_id } })
                .then(() => {
                    resolve();
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    reject({
                        error: error as ApiError,
                    });
                });
        });
    }
}

export default PollAsyncTask;
